// ============================================================
// discussionUtils.js — Fonctions utilitaires pures
// Chemin : src/components/Discussion/utils/discussionUtils.js
// ============================================================

import React from 'react';

// ────────────────────────────────────────────────────────────
// Table des locales
// ────────────────────────────────────────────────────────────
const LOCALES = {
  fr: 'fr-FR', en: 'en-US', de: 'de-DE', es: 'es-ES', pt: 'pt-PT',
  it: 'it-IT', hu: 'hu-HU', ua: 'uk-UA', sv: 'sv-SE', nl: 'nl-NL',
  fi: 'fi-FI', pl: 'pl-PL', ro: 'ro-RO', el: 'el-GR', no: 'no-NO',
  da: 'da-DK', cs: 'cs-CZ', sk: 'sk-SK', hr: 'hr-HR', lt: 'lt-LT',
  et: 'et-EE', lv: 'lv-LV', bg: 'bg-BG', sl: 'sl-SI', sr: 'sr-RS', is: 'is-IS', mt: 'mt-MT',
};

// ── Heure ──────────────────────────────────────────────────
export const formatTime = (isoDate, language = 'fr') => {
  const locale = LOCALES[language] || 'fr-FR';
  return new Date(isoDate).toLocaleTimeString(locale, {
    hour: '2-digit', minute: '2-digit',
  });
};

// ── Date complète ──────────────────────────────────────────
export const formatDate = (isoDate, language = 'fr') => {
  const locale = LOCALES[language] || 'fr-FR';
  return new Date(isoDate).toLocaleDateString(locale, {
    year: 'numeric', month: 'numeric', day: 'numeric',
  });
};

// ── Date courte (séparateur de groupe) ────────────────────
export const formaterDate = (isoDate, language = 'fr') => {
  const locale = LOCALES[language] || 'fr-FR';
  return new Date(isoDate).toLocaleDateString(locale, {
    month: 'long', day: 'numeric',
  });
};

export const isToday = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// ── Particules ambiance ────────────────────────────────────
export const generateParticles = (count = 12) =>
  Array.from({ length: count }).map(() => ({
    left:     Math.random() * 100,
    top:      Math.random() * 100,
    delay:    Math.random() * 2,
    duration: 3 + Math.random() * 2,
  }));

// ── Extraction URL YouTube (inclut Shorts) ────────────────
export const extractYouTubeUrl = (text) => {
  if (!text) return null;
  const re = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const m  = re.exec(text);
  return m ? `https://www.youtube.com/watch?v=${m[1]}` : null;
};

// ── URLs cliquables dans un texte ─────────────────────────
export const parseTextWithLinks = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  text.replace(urlRegex, (url, index) => {
    if (index > lastIndex) parts.push(<span key={lastIndex}>{text.substring(lastIndex, index)}</span>);
    parts.push(<a key={url} href={url} target="_blank" rel="noopener noreferrer">{url}</a>);
    lastIndex = index + url.length;
    return url;
  });
  if (lastIndex < text.length) parts.push(<span key={lastIndex}>{text.substring(lastIndex)}</span>);
  return parts;
};

// ── MIME type depuis l'extension ──────────────────────────
export const getMimeType = (fileType) => {
  const map = {
    pdf:  'application/pdf',
    txt:  'text/plain',
    doc:  'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    odt:  'application/vnd.oasis.opendocument.text',
    ppt:  'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    odp:  'application/vnd.oasis.opendocument.presentation',
    xls:  'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ods:  'application/vnd.oasis.opendocument.spreadsheet',
  };
  return map[fileType] || 'application/octet-stream';
};

// ── Injection de quizz toutes les N messages ──────────────
export const buildEnhancedMessages = (messages, frequency = 15) => {
  const enhanced = [];
  messages.forEach((msg, index) => {
    enhanced.push(msg);
    if ((index + 1) % frequency === 0) {
      const themes = ['histoire', 'géographie', 'science'];
      const theme  = themes[Math.floor(Math.random() * themes.length)];
      enhanced.push({
        _id:  `quiz-${index}`,
        type: 'quiz',
        quiz: {
          title:      `Quiz ${theme.charAt(0).toUpperCase() + theme.slice(1)} 🎯`,
          buttonText: 'Jouer au quiz',
          url:        `/quiz/${theme}`,
        },
      });
    }
  });
  return enhanced;
};

// ── Labels documents ──────────────────────────────────────
export const DOC_LABELS = {
  pdf:  { icon: <i className="ti ti-file-type-pdf" style={{ fontSize: '18px' }} aria-hidden="true" />,  label: 'PDF' },
  txt:  { icon: <i className="ti ti-file-text" style={{ fontSize: '18px' }} aria-hidden="true" />,       label: 'Texte' },
  doc:  { icon: <i className="ti ti-file-type-docx" style={{ fontSize: '18px' }} aria-hidden="true" />,       label: 'Word (DOC)' },
  docx: { icon: <i className="ti ti-file-type-docx" style={{ fontSize: '18px' }} aria-hidden="true" />,       label: 'Word (DOCX)' },
  odt:  { icon: <i className="ti ti-file-type-docx" style={{ fontSize: '18px' }} aria-hidden="true" />,       label: 'Open Office (ODT)' },
  ppt:  { icon: <i className="ti ti-file-type-ppt" style={{ fontSize: '18px' }} aria-hidden="true" />,   label: 'PowerPoint (PPT)' },
  pptx: { icon: <i className="ti ti-file-type-ppt" style={{ fontSize: '18px' }} aria-hidden="true" />,   label: 'PowerPoint (PPTX)' },
  odp:  { icon: <i className="ti ti-file-type-ppt" style={{ fontSize: '18px' }} aria-hidden="true" />,   label: 'Open Office Impress (ODP)' },
  xls:  { icon: <i className="ti ti-file-type-xls" style={{ fontSize: '18px' }} aria-hidden="true" />,   label: 'Excel (XLS)' },
  xlsx: { icon: <i className="ti ti-file-type-xls" style={{ fontSize: '18px' }} aria-hidden="true" />,   label: 'Excel (XLSX)' },
  ods:  { icon: <i className="ti ti-file-type-xls" style={{ fontSize: '18px' }} aria-hidden="true" />,   label: 'Open Office Calc (ODS)' },
};

export const DOCUMENT_FIELD_BY_MIME = {
  'application/pdf': 'pdf',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.oasis.opendocument.presentation': 'odp',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.oasis.opendocument.spreadsheet': 'ods',
  'application/vnd.oasis.opendocument.text': 'odt',
  'text/plain': 'txt',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};

// Renvoie le nom de champ attendu par le serveur (files.<field>) ou null si type non géré
export function resolveFileField(mimeType) {
  if (mimeType.startsWith('image/')) return { field: 'image', fallbackName: 'image.jpg',  fallbackMime: 'image/jpeg' };
  if (mimeType.startsWith('video/')) return { field: 'video', fallbackName: 'video.mp4',  fallbackMime: 'video/mp4' };
  if (mimeType.startsWith('audio/')) return { field: 'audio', fallbackName: 'audio.mp3',  fallbackMime: 'audio/mp3' };
  const doc = DOCUMENT_FIELD_BY_MIME[mimeType];
  return doc ? { field: doc, fallbackName: `document.${doc}`, fallbackMime: mimeType } : null;
}

